import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from '@/lib/api';
import { Package, Plus, Search, Trash2, CheckCircle, Archive, MoreHorizontal, Upload, Sparkles } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { toast } from 'sonner';

const statusColors = {
  active: { bg: 'rgba(34,197,94,0.1)', text: '#16a34a' },
  draft: { bg: 'rgba(234,179,8,0.1)', text: '#ca8a04' },
  archived: { bg: 'rgba(107,114,128,0.1)', text: '#6b7280' },
};

export default function ProductListPage() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [categories, setCategories] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aiOptimizing, setAiOptimizing] = useState(false);
  const [aiProgress, setAiProgress] = useState(null);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20, admin: 'true' });
      if (search) params.set('search', search);
      if (statusFilter) params.set('status', statusFilter);
      if (categoryFilter) params.set('category', categoryFilter);
      const { data } = await api.get(`/products?${params}`);
      setProducts(data.products);
      setTotal(data.total);
      setPages(data.pages);
    } catch {
      toast.error('Failed to load products');
    }
    setLoading(false);
  }, [page, search, statusFilter, categoryFilter]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);
  useEffect(() => {
    api.get('/products/categories').then(r => setCategories(r.data)).catch(() => {});
  }, []);

  const toggleSelect = (handle) => {
    setSelected(prev => prev.includes(handle) ? prev.filter(h => h !== handle) : [...prev, handle]);
  };
  const toggleAll = () => {
    setSelected(prev => prev.length === products.length ? [] : products.map(p => p.handle));
  };

  const bulkAction = async (action) => {
    if (!selected.length) return;
    try {
      const { data } = await api.post('/products/bulk-action', { action, handles: selected });
      toast.success(data.message);
      setSelected([]);
      fetchProducts();
    } catch {
      toast.error('Action failed');
    }
  };

  const deleteProduct = async (handle) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      await api.delete(`/products/${handle}`);
      toast.success('Product deleted');
      fetchProducts();
    } catch {
      toast.error('Delete failed');
    }
  };

  const bulkAiOptimize = async () => {
    if (!selected.length) return;
    setAiOptimizing(true);
    setAiProgress({ total: selected.length, completed: 0, status: 'starting' });
    try {
      const { data } = await api.post('/ai/products/bulk-optimize', { handles: selected, fields: ['title', 'description'] });
      const jobId = data.job_id;
      const poll = setInterval(async () => {
        try {
          const { data: status } = await api.get(`/ai/products/bulk-optimize/${jobId}/status`);
          setAiProgress({ total: status.total, completed: status.completed, status: status.status });
          if (status.status === 'completed') {
            clearInterval(poll);
            const succeeded = status.results.filter(r => r.status === 'success').length;
            const failed = status.results.filter(r => r.status !== 'success').length;
            toast.success(`AI Optimization: ${succeeded} succeeded, ${failed} failed`);
            if (succeeded > 0 && window.confirm(`Apply AI optimizations to ${succeeded} products?`)) {
              await api.post(`/ai/products/bulk-optimize/${jobId}/apply`);
              toast.success('Optimizations applied!');
              fetchProducts();
            }
            setAiOptimizing(false);
            setAiProgress(null);
            setSelected([]);
          }
        } catch { clearInterval(poll); setAiOptimizing(false); setAiProgress(null); }
      }, 2000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AI optimization failed');
      setAiOptimizing(false);
      setAiProgress(null);
    }
  };

  return (
    <motion.div data-testid="product-list-page" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-serif text-2xl tracking-tight" style={{ color: '#1A1A1A' }}>Products</h1>
          <p className="font-sans text-sm mt-1" style={{ color: 'rgba(26,26,26,0.5)' }}>{total} products total</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="import-products-btn" variant="outline" onClick={() => navigate('/admin/import')}
            className="font-sans text-sm gap-2 rounded-none border-[rgba(26,26,26,0.15)]" style={{ color: '#1A1A1A' }}>
            <Upload size={15} strokeWidth={1.5} /> Import
          </Button>
          <Button data-testid="add-product-btn" onClick={() => navigate('/admin/products/new')}
            className="font-sans text-sm gap-2 rounded-none" style={{ backgroundColor: '#E85D3A', color: '#fff' }}>
            <Plus size={15} strokeWidth={1.5} /> Add Product
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1 max-w-sm">
          <Search size={15} strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'rgba(26,26,26,0.35)' }} />
          <Input data-testid="product-search" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search products..." className="pl-9 font-sans text-sm rounded-none" style={{ borderColor: 'rgba(26,26,26,0.12)' }} />
        </div>
        <Select value={statusFilter} onValueChange={v => { setStatusFilter(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger data-testid="status-filter" className="w-[140px] font-sans text-sm rounded-none" style={{ borderColor: 'rgba(26,26,26,0.12)' }}>
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
          </SelectContent>
        </Select>
        <Select value={categoryFilter} onValueChange={v => { setCategoryFilter(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger data-testid="category-filter" className="w-[160px] font-sans text-sm rounded-none" style={{ borderColor: 'rgba(26,26,26,0.12)' }}>
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Bulk Actions */}
      {selected.length > 0 && (
        <div className="flex items-center gap-3 mb-4 p-3 rounded-lg" style={{ backgroundColor: 'rgba(196,149,106,0.08)', border: '1px solid rgba(196,149,106,0.2)' }}>
          <span className="font-sans text-sm font-medium" style={{ color: '#C4956A' }}>{selected.length} selected</span>
          <Button size="sm" variant="ghost" onClick={() => bulkAction('activate')} className="font-sans text-xs gap-1" data-testid="bulk-activate">
            <CheckCircle size={13} /> Activate
          </Button>
          <Button size="sm" variant="ghost" onClick={() => bulkAction('draft')} className="font-sans text-xs gap-1" data-testid="bulk-draft">
            Draft
          </Button>
          <Button size="sm" variant="ghost" onClick={() => bulkAction('archive')} className="font-sans text-xs gap-1" data-testid="bulk-archive">
            <Archive size={13} /> Archive
          </Button>
          <Button size="sm" variant="ghost" onClick={() => bulkAction('delete')} className="font-sans text-xs gap-1 text-red-600" data-testid="bulk-delete">
            <Trash2 size={13} /> Delete
          </Button>
          <div className="ml-auto">
            <Button size="sm" onClick={bulkAiOptimize} disabled={aiOptimizing} data-testid="bulk-ai-optimize"
              className="font-sans text-xs gap-1 rounded-none" style={{ backgroundColor: '#C4956A', color: '#fff' }}>
              <Sparkles size={13} /> {aiOptimizing ? `Optimizing ${aiProgress?.completed || 0}/${aiProgress?.total || 0}...` : 'AI Optimize'}
            </Button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="rounded-lg border overflow-hidden" style={{ borderColor: 'rgba(26,26,26,0.08)', backgroundColor: '#FDFBF7' }}>
        <table className="w-full">
          <thead>
            <tr style={{ backgroundColor: 'rgba(26,26,26,0.02)' }}>
              <th className="w-10 p-3">
                <Checkbox data-testid="select-all" checked={selected.length === products.length && products.length > 0}
                  onCheckedChange={toggleAll} />
              </th>
              <th className="text-left p-3 font-sans text-xs uppercase tracking-wider font-medium" style={{ color: 'rgba(26,26,26,0.45)' }}>Product</th>
              <th className="text-left p-3 font-sans text-xs uppercase tracking-wider font-medium hidden md:table-cell" style={{ color: 'rgba(26,26,26,0.45)' }}>Category</th>
              <th className="text-left p-3 font-sans text-xs uppercase tracking-wider font-medium" style={{ color: 'rgba(26,26,26,0.45)' }}>Price</th>
              <th className="text-left p-3 font-sans text-xs uppercase tracking-wider font-medium hidden sm:table-cell" style={{ color: 'rgba(26,26,26,0.45)' }}>Stock</th>
              <th className="text-left p-3 font-sans text-xs uppercase tracking-wider font-medium" style={{ color: 'rgba(26,26,26,0.45)' }}>Status</th>
              <th className="w-10 p-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="p-12 text-center font-sans text-sm" style={{ color: 'rgba(26,26,26,0.4)' }}>Loading...</td></tr>
            ) : products.length === 0 ? (
              <tr><td colSpan={7} className="p-12 text-center">
                <Package size={32} strokeWidth={1} className="mx-auto mb-3" style={{ color: 'rgba(26,26,26,0.2)' }} />
                <p className="font-sans text-sm" style={{ color: 'rgba(26,26,26,0.4)' }}>No products found</p>
              </td></tr>
            ) : products.map(p => {
              const sc = statusColors[p.status] || statusColors.draft;
              return (
                <tr key={p.handle} className="border-t transition-colors hover:bg-[rgba(26,26,26,0.01)]" style={{ borderColor: 'rgba(26,26,26,0.06)' }}>
                  <td className="p-3"><Checkbox data-testid={`select-${p.handle}`} checked={selected.includes(p.handle)} onCheckedChange={() => toggleSelect(p.handle)} /></td>
                  <td className="p-3">
                    <Link to={`/admin/products/${p.handle}/edit`} data-testid={`edit-${p.handle}`} className="flex items-center gap-3 group">
                      <div className="w-10 h-10 rounded overflow-hidden flex-shrink-0" style={{ backgroundColor: 'rgba(26,26,26,0.04)' }}>
                        {p.images?.[0]?.url ? <img src={p.images[0].url} alt="" className="w-full h-full object-cover" /> : null}
                      </div>
                      <div className="min-w-0">
                        <p className="font-sans text-sm font-medium truncate group-hover:underline" style={{ color: '#1A1A1A' }}>{p.title}</p>
                        <p className="font-sans text-xs truncate" style={{ color: 'rgba(26,26,26,0.4)' }}>{p.sku || p.handle}</p>
                      </div>
                    </Link>
                  </td>
                  <td className="p-3 hidden md:table-cell">
                    <span className="font-sans text-sm" style={{ color: 'rgba(26,26,26,0.6)' }}>{p.category || '—'}</span>
                  </td>
                  <td className="p-3">
                    <span className="font-sans text-sm" style={{ color: '#1A1A1A' }}>${p.price?.toFixed(2)}</span>
                    {p.compare_at_price > p.price && (
                      <span className="font-sans text-xs line-through ml-1" style={{ color: 'rgba(26,26,26,0.35)' }}>${p.compare_at_price?.toFixed(2)}</span>
                    )}
                  </td>
                  <td className="p-3 hidden sm:table-cell">
                    <span className="font-sans text-sm" style={{ color: p.stock_quantity <= (p.low_stock_threshold || 5) ? '#E85D3A' : 'rgba(26,26,26,0.6)' }}>
                      {p.stock_quantity}
                    </span>
                  </td>
                  <td className="p-3">
                    <Badge className="rounded-sm font-sans text-[10px] uppercase tracking-wider font-medium" style={{ backgroundColor: sc.bg, color: sc.text, border: 'none' }}>
                      {p.status}
                    </Badge>
                  </td>
                  <td className="p-3">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button data-testid={`actions-${p.handle}`} className="p-1 rounded hover:bg-[rgba(26,26,26,0.05)] transition-colors">
                          <MoreHorizontal size={16} strokeWidth={1.5} style={{ color: 'rgba(26,26,26,0.4)' }} />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/admin/products/${p.handle}/edit`)} className="font-sans text-sm">Edit</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => window.open(`/product/${p.handle}`, '_blank')} className="font-sans text-sm">View in Store</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => deleteProduct(p.handle)} className="font-sans text-sm text-red-600">Delete</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex items-center justify-between mt-6">
          <p className="font-sans text-sm" style={{ color: 'rgba(26,26,26,0.5)' }}>
            Page {page} of {pages}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}
              className="font-sans text-xs rounded-none" data-testid="prev-page">Previous</Button>
            <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(p => p + 1)}
              className="font-sans text-xs rounded-none" data-testid="next-page">Next</Button>
          </div>
        </div>
      )}
    </motion.div>
  );
}
